import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	AnalyticsFilters,
	AnalyticsInfoResponse,
	AnalyticsInfoResponseApi
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-analytics.model';
import { advancedPostToQuery } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/advanced-filters/advanced-filters.types';
import { SlMatchTypeFilter } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/filters/match-type/match-type.component';
import { recoverUnlessStrict } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/recover-unless-strict';
import { smiUrl } from 'components';
import { map, Observable } from 'rxjs';

// Honest empty-state fallback used when the analytics endpoint errors
// out or returns nothing. All trend deltas are `null` so the FE renders
// "—" instead of misleading "+0 %" arrows; badges are also null because
// without real data we have nothing to qualify ("Good"/"Very Good"/…).
const EMPTY_ANALYTICS_INFO_RESPONSE: AnalyticsInfoResponseApi = {
	total_mentions: { value: 0, changes: null, good: false, badge: null },
	authority_score: { value: 0, changes: null, good: false, badge: null },
	total_reach: { value: 0, changes: null, good: false, badge: null },
	total_engagements: { value: 0, changes: null, good: false, badge: null },
	engagement_rate: { value: 0, changes: null, good: false, badge: null },
	reputation: 0,
	reputation_changes: null,
	reputation_good: false,
	reputation_badge: null
};

@Injectable({
	providedIn: 'root'
})
export class GetSocialListeningMyBrandAnalyticsInfoService {
	constructor(private http: HttpClient) {}

	/**
	 * `cutoff`/`to` scope the KPIs to a window; omitted ⇒ the backend's default
	 * (all data). Callers that show a date range MUST pass them — this signature
	 * previously had no way to express a window at all, so the Executive Summary
	 * slide printed all-time totals under a 30-day label, and the Analytics tab's
	 * KPI cards ignored the global date filter.
	 *
	 * Param order matches the other windowed analytics services
	 * (volume, top-countries, …): (brandId, cutoff, filters, to).
	 */
	fetch(
		brandId: string,
		cutoff?: Date | null,
		filters?: AnalyticsFilters,
		to?: Date | null,
		matchType?: SlMatchTypeFilter,
		strict = false
	): Observable<AnalyticsInfoResponse> {
		return this.fetchOrNull(
			brandId,
			cutoff,
			filters,
			to,
			matchType,
			strict
		).pipe(map(info => info ?? this.emptyResponse()));
	}

	/**
	 * The same read, with a failure reported as `null` instead of as zeros.
	 *
	 * `fetch` answers a failed request with an all-zero response so a KPI
	 * card shows "0" and "—" instead of breaking, which is right for a card.
	 * It is wrong for a caller that DECIDES something from the number: the
	 * report's thin-data gate would read a failed request as "no mentions"
	 * and blank the sections of a report that has plenty. Unknown is not
	 * zero, so this keeps them apart.
	 */
	fetchOrNull(
		brandId: string,
		cutoff?: Date | null,
		filters?: AnalyticsFilters,
		to?: Date | null,
		matchType?: SlMatchTypeFilter,
		strict = false
	): Observable<AnalyticsInfoResponse | null> {
		const urlBuilder = smiUrl()
			.add('social-listening')
			.add('brand')
			.add(brandId)
			.add('analytics-info')
			.query('from', cutoff?.toISOString())
			.query('to', to?.toISOString());
		this.applyFilters(urlBuilder, filters, matchType);
		const url = urlBuilder.done();

		return this.http.get<AnalyticsInfoResponseApi | null>(url).pipe(
			map(r => this.mapResponse(r)),
			recoverUnlessStrict(strict, () => null)
		);
	}

	/** What `fetch` answers a failed read with: every figure zero, every
	 *  change unknown. */
	emptyResponse(): AnalyticsInfoResponse {
		return this.mapResponse(EMPTY_ANALYTICS_INFO_RESPONSE);
	}

	private applyFilters(
		b: any,
		filters?: AnalyticsFilters,
		matchType?: SlMatchTypeFilter
	): void {
		if (filters?.platforms?.length)
			b.query('platforms', filters.platforms.join(','));
		if (filters?.sentiments?.length)
			b.query('sentiments', filters.sentiments.join(','));
		if (filters?.emotions?.length)
			b.query('emotions', filters.emotions.join(','));
		if (filters?.languages?.length)
			b.query('languages', filters.languages.join(','));
		if (filters?.engagementRate) {
			b.query('engagement_rate_min', String(filters.engagementRate[0]));
			b.query('engagement_rate_max', String(filters.engagementRate[1]));
		}
		if (matchType) b.query('match_type', matchType);
		for (const [advKey, advVal] of Object.entries(
			advancedPostToQuery(filters?.advanced)
		)) {
			b.query(advKey, advVal);
		}
	}

	private mapResponse(
		r: Partial<AnalyticsInfoResponseApi> | null | undefined
	): AnalyticsInfoResponse {
		// Defensive optional access — the backend can return a partial
		// payload (or `null` body) when stats are missing; missing trend
		// deltas stay `null` so cards render "—" instead of "+0 %".
		const response = r ?? {};

		return {
			totalMentions: response.total_mentions?.value ?? 0,
			totalMentionsChanges: response.total_mentions?.changes ?? null,
			authorityScore: response.authority_score?.value ?? 0,
			authorityScoreChanges: response.authority_score?.changes ?? null,
			authorityScoreBadge: response.authority_score?.badge ?? null,
			totalReach: response.total_reach?.value ?? 0,
			totalReachChanges: response.total_reach?.changes ?? null,
			totalEngagements: response.total_engagements?.value ?? 0,
			totalEngagementsChanges:
				response.total_engagements?.changes ?? null,
			engagementRate: response.engagement_rate?.value ?? 0,
			engagementRateChanges: response.engagement_rate?.changes ?? null,
			engagementRateBadge: response.engagement_rate?.badge ?? null,
			reputation: response.reputation ?? 0,
			reputationChanges: response.reputation_changes ?? null,
			reputationBadge: response.reputation_badge ?? null,
			likes: response.likes?.value ?? 0,
			likesChanges: response.likes?.changes ?? null,
			socialMediaReach: response.social_media_reach?.value ?? 0,
			socialMediaReachChanges:
				response.social_media_reach?.changes ?? null,
			webReach: response.web_reach?.value ?? 0,
			webReachChanges: response.web_reach?.changes ?? null,
			webSites: response.web_sites?.value ?? 0,
			webSitesChanges: response.web_sites?.changes ?? null,
			socialMentions: response.social_mentions?.value ?? 0,
			socialMentionsChanges: response.social_mentions?.changes ?? null,
			webMentions: response.web_mentions?.value ?? 0,
			webMentionsChanges: response.web_mentions?.changes ?? null,
			positiveMentions: response.positive_mentions ?? 0,
			positiveShare: response.positive_share ?? null,
			positiveMentionsChanges: response.positive_mentions_changes ?? null,
			negativeMentions: response.negative_mentions ?? 0,
			negativeShare: response.negative_share ?? null,
			negativeMentionsChanges: response.negative_mentions_changes ?? null
		};
	}
}
